/*
  Slides the fill toward whatever position the server rendered, and
  celebrates a milestone the moment it is newly crossed.

  The cart drawer's markup is replaced wholesale on every cart update (Dawn's
  section rendering API swaps in fresh HTML rather than patching it), so a
  plain CSS transition has nothing to animate between old and new state —
  the new fragment arrives already at its final width. Two things fix that:

  1. The fill's width transition (in idz-cart-progress.css) needs a starting
     point to animate FROM. This script captures the previous run's percent
     before the swap and briefly re-applies it to the new element, then lets
     the CSS transition carry it forward to the real value — that is the
     "slide" the cart total's change should visibly produce.
  2. A MutationObserver detects the swap itself, since it does not care how
     the content changed, only that it did.

  "Newly crossed" is tracked in sessionStorage, keyed per milestone id, so a
  milestone already unlocked when the drawer first opens does not replay the
  celebration — only a milestone that flips from not-achieved to achieved
  during this session does.
*/
(function () {
  var PERCENT_KEY = 'idzCartRewardsPercent';
  var ACHIEVED_KEY = 'idzCartRewardsAchieved';

  // Brighter, more varied palette than the original mostly-ink/lilac set —
  // still anchored by the brand's ink and gold, but with punchier pink,
  // teal and coral mixed in so the burst reads as a celebration, not a
  // muted brand-color sprinkle.
  var CONFETTI_COLORS = [
    '#241b4e',
    '#ff6f91',
    '#ffdd8a',
    '#4ad991',
    '#5ec8f2',
    '#ff9f5a',
    '#c9a6ff',
    '#ffffff'
  ];
  var CONFETTI_COUNT = 100;

  function readJSON(key, fallback) {
    try {
      return JSON.parse(sessionStorage.getItem(key)) || fallback;
    } catch (e) {
      return fallback;
    }
  }

  function writeJSON(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* Storage unavailable — the slide-in and replay guard just won't
         persist across a drawer close/reopen, which is a fine degradation. */
    }
  }

  // The percent the liquid computes assumes each stop sits at an evenly
  // spaced i/(count-1) fraction of the rail's width — true for the fill's
  // OWN maths, but not necessarily true of where flex actually renders
  // each stop's icon, since justify-content: space-between distributes
  // space between real, unequally-sized items (a short caption vs. a long
  // one), not their centres. That mismatch is what let the cursor star
  // visually get ahead of a milestone icon it hadn't actually reached yet.
  // This measures the REAL on-screen centre of the last achieved icon and
  // the next un-achieved one, and interpolates between those two actual
  // pixel positions instead — the star is then physically incapable of
  // passing an icon before its threshold, because it never targets
  // anywhere past that icon's own measured position to begin with.
  // Returns null when the track isn't laid out yet (e.g. display:none),
  // so callers can fall back to the liquid's own assumed percent.
  function computeMeasuredPercent(track) {
    var rail = track.querySelector('[data-idz-cr-rail]');
    if (!rail) return null;
    var railRect = rail.getBoundingClientRect();
    if (!railRect.width) return null;

    var stops = track.querySelectorAll('[data-idz-cr-marker]');
    if (!stops.length) return null;

    var spend = parseFloat(track.getAttribute('data-idz-cr-spend-cents')) || 0;

    var prevPos = 0;
    var prevThreshold = 0;
    var nextPos = null;
    var nextThreshold = null;

    for (var i = 0; i < stops.length; i++) {
      var stop = stops[i];
      var badge = stop.querySelector('.idz-cr__marker-badge');
      if (!badge) continue;
      var badgeRect = badge.getBoundingClientRect();
      var centerPercent = ((badgeRect.left + badgeRect.width / 2) - railRect.left) / railRect.width * 100;
      var achieved = stop.getAttribute('data-idz-cr-achieved') === 'true';
      var threshold = parseFloat(stop.getAttribute('data-idz-cr-threshold-cents')) || 0;

      if (achieved) {
        prevPos = centerPercent;
        prevThreshold = threshold;
      } else if (nextPos === null) {
        nextPos = centerPercent;
        nextThreshold = threshold;
      }
    }

    // Every milestone met — no "next" icon left to stay behind, so the
    // fill just runs the rail's full length rather than stopping short at
    // the last icon's own (slightly inset-from-the-edge) measured centre.
    if (nextPos === null) return 100;

    var span = nextThreshold - prevThreshold;
    var fraction = span > 0 ? (spend - prevThreshold) / span : 1;
    if (fraction < 0) fraction = 0;
    if (fraction > 1) fraction = 1;

    return prevPos + (nextPos - prevPos) * fraction;
  }

  // Builds the exact same hard-edged gradient the liquid renders inline,
  // for a given in-between percent — the rail has no separate "fill" box
  // any more (see idz-cart-progress.css's header comment for why: a
  // child box's percentage WIDTH silently failing to resolve against its
  // parent's is exactly the class of bug that kept this not painting
  // through several previous fixes). A gradient is always 100% of its own
  // box, so there's nothing left in the chain that can quietly fail.
  function railGradient(rail, percent) {
    var ink = rail.getAttribute('data-idz-cr-ink') || '#241b4e';
    var bg = rail.getAttribute('data-idz-cr-track-bg') || '#ffffff';
    return (
      'linear-gradient(to right, ' +
      ink + ' 0%, ' + ink + ' ' + percent + '%, ' +
      bg + ' ' + percent + '%, ' + bg + ' 100%)'
    );
  }

  // easeInOutCubic — matches the visual weight of the old CSS
  // cubic-bezier(0.65, 0, 0.35, 1) transition closely enough, now run by
  // hand since a CSS `transition` has nothing to animate between when
  // this element is a fresh one on every cart update (see this file's
  // header comment).
  function ease(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function animateSlide(track) {
    var rail = track.querySelector('[data-idz-cr-rail]');
    if (!rail) return;
    var cursor = track.querySelector('[data-idz-cr-cursor]');

    var targetPercent = computeMeasuredPercent(track);
    if (targetPercent === null) {
      targetPercent = parseFloat(track.getAttribute('data-idz-cr-percent')) || 0;
    }
    var lastPercent = readJSON(PERCENT_KEY, null);
    writeJSON(PERCENT_KEY, targetPercent);

    if (lastPercent === null || lastPercent === targetPercent) {
      // Nothing to animate from — just make sure the correct value is
      // applied and stop.
      rail.style.background = railGradient(rail, targetPercent);
      if (cursor) cursor.style.left = targetPercent + '%';
      return;
    }

    var start = lastPercent;
    var duration = 700;
    var startTime = null;

    function frame(now) {
      if (startTime === null) startTime = now;
      var t = Math.min((now - startTime) / duration, 1);
      var value = start + (targetPercent - start) * ease(t);
      rail.style.background = railGradient(rail, value);
      if (cursor) cursor.style.left = value + '%';
      if (t < 1) {
        requestAnimationFrame(frame);
      } else {
        // Land exactly on the real target — floating-point easing can
        // leave this a hair short of it otherwise.
        rail.style.background = railGradient(rail, targetPercent);
        if (cursor) cursor.style.left = targetPercent + '%';
      }
    }
    requestAnimationFrame(frame);
  }

  // 'mark' (a masked sparkle shape) used to be in this list. Dropped for
  // the same reason the drop-shadow filter was removed elsewhere in this
  // file: `mask-image` forces iOS Safari into slow per-frame software
  // compositing on every animating instance, and with up to CONFETTI_COUNT
  // pieces potentially using it at once, it was a live recurrence of the
  // exact stutter that was just fixed, just on a different shape.
  var CONFETTI_SHAPES = ['ribbon', 'ribbon', 'dot'];

  function confettiPiece(container, index, panelWidth, panelHeight, originY) {
    var shape = CONFETTI_SHAPES[Math.floor(Math.random() * CONFETTI_SHAPES.length)];
    var piece = document.createElement('span');
    piece.className = 'idz-cr__confetti-piece idz-cr__confetti-piece--' + shape;

    piece.style.setProperty('--idz-cf-color', CONFETTI_COLORS[index % CONFETTI_COLORS.length]);
    // Total duration is a balance, not just "longer" — the launch (first
    // 23.5% of the keyframe timeline, see idz-cart-progress.css) needs real
    // wall-clock time. 2.15-2.8s carried over unchanged from the last pass.
    piece.style.setProperty('--idz-cf-duration', (2.15 + Math.random() * 0.65).toFixed(2) + 's');
    // Staggered start so pieces keep arriving for a moment rather than
    // firing as one flat, all-at-once wave — kept short so the burst still
    // reads as immediate, not a slow trickle.
    piece.style.setProperty('--idz-cf-delay', (Math.random() * 0.2).toFixed(2) + 's');

    // One burst, spread across the full width of the panel — not a narrow
    // stream, each piece gets its own random horizontal target across
    // nearly the whole width so the spray reads as wide as the card itself.
    // Launched from originY (set low, near the bottom of the item list, so
    // the upward leg actually has distance to cover): a fast near-vertical
    // launch out to just below the true top edge (the "explosion hits the
    // ceiling"), then gravity takes over for a fall from there down well
    // past the bottom. Both legs are computed from real, absolute panel
    // positions (not a fixed fraction of panel height) so they land
    // correctly regardless of where originY actually sits.
    var burstX = (Math.random() - 0.5) * panelWidth * 0.95;
    var topMargin = 16 + Math.random() * 40;
    var burstY = topMargin - originY;
    var endX = burstX + (Math.random() - 0.5) * 40;
    var bottomOvershoot = panelHeight * (1.08 + Math.random() * 0.18);
    var endY = bottomOvershoot - originY;

    piece.style.setProperty('--idz-cf-burst-x', burstX.toFixed(0) + 'px');
    piece.style.setProperty('--idz-cf-burst-y', burstY.toFixed(0) + 'px');
    piece.style.setProperty('--idz-cf-end-x', endX.toFixed(0) + 'px');
    piece.style.setProperty('--idz-cf-end-y', endY.toFixed(0) + 'px');

    piece.style.setProperty('--idz-cf-rot-1', Math.round(Math.random() * 140 - 70) + 'deg');
    piece.style.setProperty('--idz-cf-rot-3', Math.round(420 + Math.random() * 260) + 'deg');

    if (shape === 'dot') {
      var dotSize = (0.5 + Math.random() * 0.4).toFixed(2) + 'rem';
      piece.style.setProperty('--idz-cf-w', dotSize);
      piece.style.setProperty('--idz-cf-h', dotSize);
    } else if (shape === 'mark') {
      var markSize = (0.9 + Math.random() * 0.5).toFixed(2) + 'rem';
      piece.style.setProperty('--idz-cf-w', markSize);
      piece.style.setProperty('--idz-cf-h', markSize);
    } else {
      piece.style.setProperty('--idz-cf-w', (0.6 + Math.random() * 0.3).toFixed(2) + 'rem');
      piece.style.setProperty('--idz-cf-h', (1.1 + Math.random() * 0.6).toFixed(2) + 'rem');
    }

    container.appendChild(piece);
  }

  function confetti() {
    var panel = document.querySelector('#CartDrawer .drawer__inner');
    if (!panel) return;

    // Appended to <body>, not `panel` — see the comment on .idz-cr__confetti
    // in idz-cart-progress.css for why (a free-gift unlock's own section
    // re-render used to destroy this mid-animation when it lived inside the
    // panel). Any leftover burst is found by class on body instead.
    var existing = document.querySelector('body > .idz-cr__confetti');
    if (existing) existing.remove();

    var panelRect = panel.getBoundingClientRect();
    var panelWidth = Math.max(panelRect.width, 280);
    var panelHeight = Math.max(panelRect.height, 400);

    // Launched from low in the visible item list, not its middle — the
    // header above and the price summary/badges/checkout button below both
    // eat real vertical space, so this measures around that chrome first,
    // then starts the burst near the bottom of what's left. Starting low
    // gives the upward leg real distance to cover before it "hits the
    // ceiling", instead of arriving there almost instantly.
    var header = panel.querySelector('.drawer__header');
    var footer = panel.querySelector('.drawer__footer');
    var headerH = header ? header.offsetHeight : 0;
    var footerH = footer ? footer.offsetHeight : 0;
    var itemsAreaHeight = Math.max(panelHeight - headerH - footerH, 0);
    var originY = headerH + itemsAreaHeight * 0.8;

    var container = document.createElement('div');
    container.className = 'idz-cr__confetti';
    // Fixed to the panel's own on-screen rect at creation time (not inset:
    // 0, now that this lives on <body> rather than inside the panel) —
    // matches the exact footprint .drawer__inner used to give it for free
    // via absolute positioning.
    container.style.top = panelRect.top + 'px';
    container.style.left = panelRect.left + 'px';
    container.style.width = panelRect.width + 'px';
    container.style.height = panelRect.height + 'px';
    container.style.setProperty('--idz-cf-origin-y', originY + 'px');
    container.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < CONFETTI_COUNT; i++) {
      confettiPiece(container, i, panelWidth, panelHeight, originY);
    }
    document.body.appendChild(container);

    // Matches the worst case (max duration 2.8s + max stagger delay 0.2s)
    // plus a short buffer.
    window.setTimeout(function () {
      container.remove();
    }, 3400);
  }

  function popMarker(marker) {
    marker.classList.remove('idz-cr-celebrate');
    void marker.offsetWidth;
    marker.classList.add('idz-cr-celebrate');
  }

  // Same "launch point" confetti() computes for its own burst — low in the
  // visible item list (see the comment inside confetti() for why) — but in
  // real VIEWPORT coordinates rather than panel-relative ones, since the
  // star (position: fixed, appended to <body>, not the panel) needs an
  // absolute point on screen, not a CSS custom property meant for a
  // descendant of the panel. Kept as its own small duplicate of that math
  // rather than shared, same as the rest of this file's stance on a
  // no-build-step theme with no import system.
  function confettiOrigin(panel) {
    var rect = panel.getBoundingClientRect();
    var header = panel.querySelector('.drawer__header');
    var footer = panel.querySelector('.drawer__footer');
    var headerH = header ? header.offsetHeight : 0;
    var footerH = footer ? footer.offsetHeight : 0;
    var itemsAreaHeight = Math.max(rect.height - headerH - footerH, 0);
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + headerH + itemsAreaHeight * 0.8,
    };
  }

  // A big star starts fully zoomed in at the confetti burst's own launch
  // point — the same spot the confetti explosion would rise from — then
  // shrinks and flies from there into the exact spot the just-achieved
  // milestone's badge sits at, landing right as the badge itself pops
  // (popMarker, fired on arrival). `onLand`, if given, fires once landing
  // is done — used by check() below to hold confetti back until the star
  // has actually finished, rather than firing it alongside or ahead of the
  // star. `position: fixed` + viewport coordinates (not relative to the
  // panel) so this works unchanged whichever scroll position the marker is
  // currently at inside the drawer's scrollable item list.
  // The drawer's own open transition (component-cart-drawer.css —
  // `.drawer.active .drawer__inner { transform: translateX(0); transition:
  // transform var(--duration-default) ease; }`) is very often STILL
  // sliding in at the exact moment a milestone gets crossed, since adding
  // an item both opens the drawer and can cross a threshold in the same
  // action. Measuring the badge's position mid-slide
  // (getBoundingClientRect reflects wherever the panel happens to be at
  // that instant, not its final resting spot) is exactly what sent the
  // star to "random" positions/directions — it was flying at a transient
  // snapshot of a still-moving target, sometimes while the panel was still
  // mostly off-screen.
  //
  // Rather than special-casing that one specific transition (fragile if
  // the duration/easing token ever changes, or if some OTHER animation is
  // moving the panel), this just polls the panel's own on-screen position
  // across animation frames and waits until it stops changing — true
  // regardless of WHY it might still be moving. Costs at most 2 frames
  // (~33ms, imperceptible) when the panel is already at rest, which is the
  // common case (drawer already open, another item crosses a later
  // milestone).
  function whenPanelSettled(panel, cb) {
    var lastLeft = null;
    var lastTop = null;
    var stableFrames = 0;
    var frames = 0;
    var maxFrames = 40; // ~0.66s safety cap at 60fps

    function step() {
      frames++;
      var rect = panel.getBoundingClientRect();
      if (lastLeft !== null && Math.abs(rect.left - lastLeft) < 0.5 && Math.abs(rect.top - lastTop) < 0.5) {
        stableFrames++;
      } else {
        stableFrames = 0;
      }
      lastLeft = rect.left;
      lastTop = rect.top;

      if (stableFrames >= 2 || frames >= maxFrames) {
        cb();
        return;
      }
      window.requestAnimationFrame(step);
    }

    window.requestAnimationFrame(step);
  }

  // Matches .idz-cr__milestone-star's own `animation: idz-cr-star-fly 0.9s
  // ...` duration (idz-cart-progress.css) — kept as its own constant here
  // since onAlmostLand (below) needs to fire at a fraction of that same
  // real duration, not the animationend event itself.
  var STAR_FLIGHT_MS = 900;

  function starFlyToMarker(marker, onAlmostLand) {
    var panel = document.querySelector('#CartDrawer .drawer__inner');
    if (!panel) {
      if (onAlmostLand) onAlmostLand();
      return;
    }

    whenPanelSettled(panel, function () {
      // Re-checked now, after any wait above — the marker can only ever be
      // one of the stops actually inside this cart drawer's own progress
      // track (data-idz-cr-marker only exists in idz-cart-progress.liquid,
      // rendered once, inside this exact panel), so a still-connected
      // marker's rect at this point is always ITS OWN real position, never
      // another milestone's.
      if (!marker.isConnected) {
        if (onAlmostLand) onAlmostLand();
        return;
      }

      var origin = confettiOrigin(panel);
      var markerRect = marker.getBoundingClientRect();

      var star = document.createElement('div');
      star.className = 'idz-cr__milestone-star';
      star.setAttribute('aria-hidden', 'true');
      star.innerHTML =
        '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 1.5l3.1 7.2 7.8.7-5.9 5.1 1.8 7.6L12 18.3l-6.8 3.8 1.8-7.6-5.9-5.1 7.8-.7z"/></svg>';
      star.style.left = origin.x + 'px';
      star.style.top = origin.y + 'px';
      star.style.setProperty('--idz-star-dx', markerRect.left + markerRect.width / 2 - origin.x + 'px');
      star.style.setProperty('--idz-star-dy', markerRect.top + markerRect.height / 2 - origin.y + 'px');

      // Fires confetti partway through the star's flight (20%) rather than
      // waiting for the full landing — reads as the confetti greeting the
      // star as it arrives instead of visibly trailing it.
      window.setTimeout(function () {
        if (onAlmostLand) onAlmostLand();
      }, Math.round(STAR_FLIGHT_MS * 0.2));

      var landed = false;
      function land() {
        if (landed) return;
        landed = true;
        star.remove();
        marker.classList.remove('idz-cr-star-pending');
        popMarker(marker);
      }
      // animationend is the normal path; the timeout is only a safety net
      // in case the element gets detached (e.g. the drawer closes
      // mid-flight) and never fires the event.
      star.addEventListener('animationend', land);
      window.setTimeout(land, STAR_FLIGHT_MS + 100);

      document.body.appendChild(star);
    });
  }

  /*
    Auto-adds/removes the actual free-gift line item the moment a free_gift
    milestone is crossed either way — this is the part that was previously
    entirely missing: the bar only ever showed "unlocked", nothing was ever
    put in the cart to back that up. Each qualifying marker carries the
    gift's variant id in data-idz-cr-gift-variant (see idz-cart-progress.liquid,
    only set when the merchant has picked a Gift product for that tier).

    Same section-swap technique idz-cart.js's own add-to-cart flow uses
    (duplicated rather than shared — this file has no build step to import
    from, and the theme has no bundler), so the drawer, its footer, and the
    header cart bubble all refresh together after the sync.
  */
  var idzCrGiftSyncInFlight = false;
  var idzCrGiftSyncPending = false;

  function renderCartSections(cartEl, data) {
    if (!data || !data.sections || typeof cartEl.getSectionsToRender !== 'function') return;
    var empty = typeof data.item_count === 'number' && data.item_count === 0;
    cartEl.classList.toggle('is-empty', empty);
    var inner = cartEl.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('is-empty', empty);
    var items = cartEl.querySelector('cart-drawer-items');
    if (items) items.classList.toggle('is-empty', empty);

    cartEl.getSectionsToRender().forEach(function (section) {
      var target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!target || !data.sections[section.id]) return;
      var parsed = new DOMParser()
        .parseFromString(data.sections[section.id], 'text/html')
        .querySelector(section.selector || '.shopify-section');
      if (parsed) target.innerHTML = parsed.innerHTML;
    });

    // Replacing #CartDrawer's innerHTML above destroys and recreates
    // #CartDrawer-Overlay — along with the click-to-close listener Dawn's
    // cart-drawer.js bound to the OLD element in its constructor — and
    // drops back to Shopify's own real, un-grouped cart rows, undoing
    // idz-box-cart-groups.js's "build your own box" merge until its own
    // MutationObserver gets around to re-running it. A gift auto-added
    // here happens on its own schedule (not a customer action against a
    // specific row), so without doing both of these explicitly, a shopper
    // could see a merged box row visibly split back into its individual
    // products right as a gift unlocks.
    if (typeof cartEl.close === 'function') {
      var overlay = cartEl.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', cartEl.close.bind(cartEl));
    }
    if (window.IdzBoxCartGroups) window.IdzBoxCartGroups.run();
  }

  function syncGifts() {
    if (idzCrGiftSyncInFlight) {
      idzCrGiftSyncPending = true;
      return;
    }

    var markers = document.querySelectorAll('[data-idz-cr-gift-variant]');
    if (!markers.length) return;

    var cartEl = document.querySelector('cart-drawer');
    if (!cartEl || typeof cartEl.getSectionsToRender !== 'function') return;
    if (!window.routes || !window.routes.cart_update_url) return;

    // Read straight off the server-rendered cart state (see
    // idz-cart-progress.liquid) instead of fetching /cart.js first — the
    // drawer is already freshly re-rendered on every cart change, so this
    // is always accurate, and skipping that extra round trip is most of
    // what was making the gift noticeably lag behind the confetti.
    var stateEl = document.querySelector('[data-idz-cr-cart-state]');
    var presentQty = {};
    if (stateEl) {
      try {
        var parsed = JSON.parse(stateEl.textContent);
        Object.keys(parsed).forEach(function (variantId) {
          presentQty[variantId] = parsed[variantId];
        });
      } catch (e) {
        /* Malformed/missing — treated as an empty cart below, same as a
           genuinely empty one; the next real cart change re-renders it. */
      }
    }

    var updates = {};
    var needsUpdate = false;
    markers.forEach(function (marker) {
      var variantId = marker.getAttribute('data-idz-cr-gift-variant');
      if (!variantId) return;
      var achieved = marker.getAttribute('data-idz-cr-achieved') === 'true';
      var currentQty = presentQty[variantId] || 0;
      // Only ever 0 or 1 — a gift tier is unlocked or it isn't, there is no
      // notion of "more" of a mystery box to add.
      if (achieved && currentQty < 1) {
        updates[variantId] = 1;
        needsUpdate = true;
      } else if (!achieved && currentQty > 0) {
        updates[variantId] = 0;
        needsUpdate = true;
      }
    });

    if (!needsUpdate) return;

    idzCrGiftSyncInFlight = true;
    var sections = cartEl.getSectionsToRender().map(function (section) {
      return section.id;
    });

    fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ updates: updates, sections: sections, sections_url: window.location.pathname })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        renderCartSections(cartEl, data);
      })
      .catch(function () {
        // A failed sync just leaves the cart as it was — nothing here is
        // worth surfacing an error for; the next cart change retries it.
      })
      .finally(function () {
        idzCrGiftSyncInFlight = false;
        if (idzCrGiftSyncPending) {
          idzCrGiftSyncPending = false;
          syncGifts();
        }
      });
  }

  // Keeps a gentle shimmer on whichever not-yet-reached milestone is
  // NEXT (the first un-achieved one in order — every stop after it is
  // further away and gets no shimmer, only the immediate next goal does),
  // to draw the eye toward the one thing actually worth adding more items
  // for right now. Re-run on every check() so it moves along automatically
  // as milestones get crossed, cart contents change, or the drawer
  // re-renders with fresh markup (a full section swap loses any class we
  // set last time, hence re-applying here rather than once on load).
  function markNextMilestone(markers) {
    var found = false;
    markers.forEach(function (marker) {
      var achieved = marker.getAttribute('data-idz-cr-achieved') === 'true';
      var isNext = !achieved && !found;
      marker.classList.toggle('idz-cr__stop--next', isNext);
      if (isNext) found = true;
    });
  }

  function check() {
    var track = document.querySelector('[data-idz-cr-track]');
    if (!track) return;

    animateSlide(track);

    var markers = document.querySelectorAll('[data-idz-cr-marker]');
    if (!markers.length) return;

    markNextMilestone(markers);

    var previous = readJSON(ACHIEVED_KEY, {});
    var next = {};
    var justAchievedMarkers = [];

    markers.forEach(function (marker) {
      var id = marker.getAttribute('data-idz-cr-marker');
      var achieved = marker.getAttribute('data-idz-cr-achieved') === 'true';
      next[id] = achieved;

      if (achieved && previous[id] === false) {
        justAchievedMarkers.push(marker);
        // Holds the badge back at its not-yet-achieved look (see the CSS)
        // even though .is-achieved is already on this element server-side
        // — removed again in land() below, once the star has actually
        // arrived, so the badge only reads as "done" once the celebration
        // that's supposed to announce that has actually finished.
        marker.classList.add('idz-cr-star-pending');
      }
    });

    if (justAchievedMarkers.length) {
      // Star first, confetti once every star is ALMOST there (85% of its
      // flight — see starFlyToMarker's onAlmostLand param) — not alongside/
      // ahead of the star launch, and not held back all the way to the
      // actual landing either (that read as the confetti visibly trailing
      // the star instead of greeting it on arrival). starsPending tracks
      // however many stars are still mid-flight (normally 1, but two
      // milestones can both cross on the same cart change) so confetti
      // fires exactly once, once the LAST one is about to land.
      var starsPending = justAchievedMarkers.length;
      justAchievedMarkers.forEach(function (marker) {
        starFlyToMarker(marker, function () {
          starsPending -= 1;
          if (starsPending <= 0) confetti();
        });
      });
    }

    writeJSON(ACHIEVED_KEY, next);

    syncGifts();
  }

  check();

  var target = document.querySelector('#CartDrawer') || document.body;
  new MutationObserver(check).observe(target, {
    childList: true,
    subtree: true,
  });
})();
